import type { DoorConfig, SignConfig } from '../game/worldConstants';
import type { WorldLayout } from '../game/worldLayout';
import type {
  AnnotationCategory,
  AnnotationTilePoint,
  WorldAnnotation,
} from '../types/worldAnnotation';
import {
  DEFAULT_INTERACTION_RADIUS,
  clampTile,
  normalizeAnnotationRecord,
  normalizeNameToken,
  sanitizeString,
  tokenizeName,
} from './worldAnnotationNormalizers';
import { clampAnnotationDraftRecord } from './worldAnnotationDraftClamp';

const isReadonlyArrayOf = <T>(value: readonly T[] | null | undefined): value is readonly T[] =>
  Array.isArray(value);

export const normalizeWorldAnnotations = (
  annotations: readonly WorldAnnotation[] | null | undefined,
  layout: WorldLayout,
): WorldAnnotation[] => {
  if (!isReadonlyArrayOf(annotations)) {
    return [];
  }

  const byId = new Map<string, WorldAnnotation>();
  annotations.forEach((annotation) => {
    const normalized = normalizeAnnotationRecord(annotation, layout);
    if (!normalized || byId.has(normalized.id)) {
      return;
    }
    byId.set(normalized.id, normalized);
  });

  return [...byId.values()];
};

export const adaptLegacySignsToAnnotations = (
  signs: readonly SignConfig[] | null | undefined,
  layout: WorldLayout,
): WorldAnnotation[] => {
  if (!isReadonlyArrayOf(signs)) {
    return [];
  }
  return normalizeWorldAnnotations(
    signs.map((sign) => ({
      id: sanitizeString(sign.id) || `sign-${sign.tileX}-${sign.tileY}`,
      name: 'Sign',
      description: sanitizeString(sign.text),
      category: 'sign',
      shape: 'point',
      position: {
        x: clampTile(sign.tileX, layout.tileWidth - 1),
        y: clampTile(sign.tileY, layout.tileHeight - 1),
      },
      playerVisible: true,
      examineText: sanitizeString(sign.text),
      interactionRadius: DEFAULT_INTERACTION_RADIUS,
      legacyType: 'sign',
      createdBy: 'system',
      createdAt: 0,
    })),
    layout,
  );
};

export const adaptLegacyDoorsToAnnotations = (
  doors: readonly DoorConfig[] | null | undefined,
  layout: WorldLayout,
): WorldAnnotation[] => {
  if (!isReadonlyArrayOf(doors)) {
    return [];
  }
  return normalizeWorldAnnotations(
    doors.map((door) => ({
      id: sanitizeString(door.id) || `door-${door.tileX}-${door.tileY}`,
      name: sanitizeString(door.label) || 'Door',
      description:
        sanitizeString(door.label) ||
        `Door to ${sanitizeString(door.targetZone) || 'another area'}.`,
      category: 'door',
      shape: 'point',
      position: {
        x: clampTile(door.tileX, layout.tileWidth - 1),
        y: clampTile(door.tileY, layout.tileHeight - 1),
      },
      playerVisible: true,
      interactionRadius: DEFAULT_INTERACTION_RADIUS,
      legacyType: 'door',
      doorTarget: {
        tileX: clampTile(door.targetTileX, layout.tileWidth - 1),
        tileY: clampTile(door.targetTileY, layout.tileHeight - 1),
        zone: sanitizeString(door.targetZone) || 'default',
      },
      createdBy: 'system',
      createdAt: 0,
    })),
    layout,
  );
};

export const mergeWorldAnnotations = (params: {
  annotations: readonly WorldAnnotation[] | null | undefined;
  signs?: readonly SignConfig[] | null | undefined;
  doors?: readonly DoorConfig[] | null | undefined;
  layout: WorldLayout;
}): WorldAnnotation[] => {
  const authored = normalizeWorldAnnotations(params.annotations, params.layout);
  const byId = new Map(authored.map((annotation) => [annotation.id, annotation]));
  [
    ...adaptLegacySignsToAnnotations(params.signs, params.layout),
    ...adaptLegacyDoorsToAnnotations(params.doors, params.layout),
  ].forEach((annotation) => {
    if (!byId.has(annotation.id)) {
      byId.set(annotation.id, annotation);
    }
  });
  return [...byId.values()];
};

export const getAnnotationAnchorTile = (annotation: WorldAnnotation): AnnotationTilePoint => {
  if (annotation.shape === 'rect' && annotation.size) {
    return {
      x: annotation.position.x + Math.floor(annotation.size.width / 2),
      y: annotation.position.y + Math.floor(annotation.size.height / 2),
    };
  }
  return { ...annotation.position };
};

export const getAnnotationCoveredTiles = (annotation: WorldAnnotation): AnnotationTilePoint[] => {
  if (annotation.shape === 'rect' && annotation.size) {
    const points: AnnotationTilePoint[] = [];
    for (let y = 0; y < annotation.size.height; y += 1) {
      for (let x = 0; x < annotation.size.width; x += 1) {
        points.push({ x: annotation.position.x + x, y: annotation.position.y + y });
      }
    }
    return points;
  }

  if (annotation.shape === 'circle') {
    const radius = Math.max(1, Math.floor(annotation.radius ?? 1));
    const points: AnnotationTilePoint[] = [];
    for (let y = -radius; y <= radius; y += 1) {
      for (let x = -radius; x <= radius; x += 1) {
        if (x * x + y * y <= radius * radius) {
          points.push({ x: annotation.position.x + x, y: annotation.position.y + y });
        }
      }
    }
    return points;
  }

  return [{ ...annotation.position }];
};

export const annotationContainsTile = (
  annotation: WorldAnnotation,
  tileX: number,
  tileY: number,
): boolean => {
  if (annotation.shape === 'rect' && annotation.size) {
    return (
      tileX >= annotation.position.x &&
      tileX < annotation.position.x + annotation.size.width &&
      tileY >= annotation.position.y &&
      tileY < annotation.position.y + annotation.size.height
    );
  }

  if (annotation.shape === 'circle') {
    const radius = Math.max(1, Math.floor(annotation.radius ?? 1));
    const deltaX = tileX - annotation.position.x;
    const deltaY = tileY - annotation.position.y;
    return deltaX * deltaX + deltaY * deltaY <= radius * radius;
  }

  return annotation.position.x === tileX && annotation.position.y === tileY;
};

export const buildAnnotationId = (prefix: string): string =>
  `${prefix}-${crypto.randomUUID().slice(0, 8)}`;

export const buildEditorAnnotation = (params: {
  category?: AnnotationCategory;
  createdAt?: number;
  endTile?: AnnotationTilePoint;
  startTile: AnnotationTilePoint;
}): WorldAnnotation => {
  const minX = Math.min(params.startTile.x, params.endTile?.x ?? params.startTile.x);
  const minY = Math.min(params.startTile.y, params.endTile?.y ?? params.startTile.y);
  const maxX = Math.max(params.startTile.x, params.endTile?.x ?? params.startTile.x);
  const maxY = Math.max(params.startTile.y, params.endTile?.y ?? params.startTile.y);
  const width = maxX - minX + 1;
  const height = maxY - minY + 1;
  const isRect = width > 1 || height > 1;
  const createdAt = params.createdAt ?? Date.now();

  return {
    id: buildAnnotationId('annotation'),
    name: isRect ? 'New Area' : 'New Annotation',
    description: '',
    category: params.category ?? (isRect ? 'area' : 'landmark'),
    shape: isRect ? 'rect' : 'point',
    position: { x: minX, y: minY },
    ...(isRect ? { size: { width, height } } : {}),
    createdBy: 'editor',
    createdAt,
  };
};

export const clampAnnotationsToLayout = (
  annotations: WorldAnnotation[],
  layout: WorldLayout,
): WorldAnnotation[] => {
  const byId = new Map<string, WorldAnnotation>();
  annotations.forEach((annotation) => {
    const clamped = clampAnnotationDraftRecord(annotation, layout);
    if (!clamped || byId.has(clamped.id)) {
      return;
    }
    byId.set(clamped.id, clamped);
  });
  return [...byId.values()];
};

interface AnnotationNameMatch {
  annotation: WorldAnnotation;
  score: number;
}

const scoreAnnotationName = (query: string, annotation: WorldAnnotation): number => {
  const normalizedQuery = normalizeNameToken(query);
  const normalizedName = normalizeNameToken(annotation.name);
  if (!normalizedQuery || !normalizedName) {
    return 0;
  }
  if (normalizedQuery === normalizedName) {
    return 100;
  }
  if (normalizedName.includes(normalizedQuery)) {
    return 80;
  }

  const filteredQuery = tokenizeName(query).join(' ');
  const filteredName = tokenizeName(annotation.name).join(' ');
  if (filteredQuery && filteredName) {
    if (filteredQuery === filteredName) {
      return 100;
    }
    if (filteredName.includes(filteredQuery)) {
      return 80;
    }
  }

  const queryTokens = new Set(filteredQuery.split(' ').filter(Boolean));
  const nameTokens = filteredName.split(' ').filter(Boolean);
  if (queryTokens.size === 0 || nameTokens.length === 0) {
    return 0;
  }

  let overlap = 0;
  nameTokens.forEach((token) => {
    if (queryTokens.has(token)) {
      overlap += 1;
    }
  });
  if (overlap === 0) {
    return 0;
  }

  return Math.round((overlap / Math.max(queryTokens.size, nameTokens.length)) * 60);
};

export const resolveAnnotationNameMatch = (
  annotations: WorldAnnotation[],
  query: string,
): {
  match: WorldAnnotation | null;
  ambiguous: WorldAnnotation[];
  suggestions: string[];
} => {
  const matches: AnnotationNameMatch[] = annotations
    .map((annotation) => ({ annotation, score: scoreAnnotationName(query, annotation) }))
    .filter((entry) => entry.score > 0)
    .sort(
      (left, right) =>
        right.score - left.score || left.annotation.name.localeCompare(right.annotation.name),
    );

  if (matches.length === 0) {
    return {
      match: null,
      ambiguous: [],
      suggestions: annotations
        .slice()
        .sort((left, right) => left.name.localeCompare(right.name))
        .slice(0, 5)
        .map((annotation) => annotation.name),
    };
  }

  const best = matches[0];
  const ambiguous = matches
    .filter((entry) => entry.score === best.score)
    .map((entry) => entry.annotation);

  if (!best || best.score < 30) {
    return {
      match: null,
      ambiguous: [],
      suggestions: matches.slice(0, 5).map((entry) => entry.annotation.name),
    };
  }

  if (ambiguous.length > 1) {
    return {
      match: null,
      ambiguous,
      suggestions: ambiguous.map((annotation) => annotation.name),
    };
  }

  return {
    match: best.annotation,
    ambiguous: [],
    suggestions: matches.slice(0, 5).map((entry) => entry.annotation.name),
  };
};
